// Libs
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useHistory } from 'react-router-dom';
// Config
import ToolbarBuilderConfig from './toolbar.config';
import KEYWORDS from '../keywords';
// Actions
import { toggleDatasource, deactivateElementViewer } from 'core/actions';
// Hooks
import { useTheme } from 'hooks';
// Selectors
import { selectSelectedSubModule } from 'components/Modules/selectors';
import { selectProfile } from 'screens/Login/selectors';
// Utils
import MODULE_KEYWORDS from 'components/Modules/keywords';
import { canShowLegacyBillingSummaryAction } from 'core/ElementViewBuilder/views/Billing/Payment/paymentSummaryCapabilities';
// Components
import { Wrapper, Notifications, HamburguerIcon, BackIcon } from 'components';
import { DisplayPicker, DataFilterSearchValue } from 'core';
import ShowedPagination from '../ShowedPagination';
import {
	DataExport,
	DataResumen,
	SortPicker,
	DeleteCatalogItem,
	MoveCatalogItem,
	canShowMoveCatalogItemAction,
	DatasourceRefresh,
} from './components';
import WarehouseSeriesControlToolbarAction from 'core/ScreenBuilder/screens/WarehouseSeriesControlScreen/WarehouseSeriesControlToolbarAction';

const { ROUTES } = KEYWORDS;

const toolbars = ToolbarBuilderConfig.getAuthorizedToolbarReactNodes();
const fullToolbars = ToolbarBuilderConfig.getAuthorizedFullToolbarReactNodes();

const GoBackButton = ({ history, dispatch }) => (
	<BackIcon
		onClick={() => {
			dispatch(deactivateElementViewer());
			history.goBack();
		}}
	/>
);

const LeftSideSwitcher = ({ menuIsOpen, history, dispatch }) => {
	return (
		<Switch>
			<Route
				exact
				path={ROUTES.FEED_MODULE_ELEMENT.PATH}
				children={
					<Wrapper padding='0'>
						{!menuIsOpen && (
							<HamburguerIcon
								button
								onClick={() => dispatch(toggleDatasource())}
							/>
						)}
						<Wrapper padding='0 0 0 10px'>
							<GoBackButton history={history} dispatch={dispatch} />
						</Wrapper>
					</Wrapper>
				}
			/>
			<Route
				exact
				path={ROUTES.FEED_MODULE.PATH}
				children={
					<Wrapper padding='0'>
						{!menuIsOpen && (
							<HamburguerIcon
								button
								onClick={() => dispatch(toggleDatasource())}
							/>
						)}
						<ShowedPagination />
					</Wrapper>
				}
			/>
			<Route
				path={ROUTES.FEED.PATH}
				children={
					<Wrapper padding='0'>
						{!menuIsOpen && (
							<HamburguerIcon
								button
								onClick={() => dispatch(toggleDatasource())}
							/>
						)}
					</Wrapper>
				}
			/>
		</Switch>
	);
};

const CenterSideSwitcher = () => {
	return (
		<Switch>
			{Object.keys(toolbars).map(path => {
				const Toolbar = toolbars[path];
				if (!Toolbar) return null;
				return (
					<Route
						exact
						key={path}
						path={path}
						children={
							<Wrapper padding='0' width='100%'>
								<Toolbar />
							</Wrapper>
						}
					/>
				);
			})}
		</Switch>
	);
};

const RightSideSwitcher = ({
	datasource,
	screenKey,
	datasourceStatus,
	datasourceSingleData,
}) => {
	const selectedItems = useSelector(state => state.feed.selectedItems);
	const selectedSubModule = useSelector(selectSelectedSubModule);
	const profile = useSelector(selectProfile);
	const { BILLINGS, INVENTORY } = MODULE_KEYWORDS.MODULES.ITEMS.KEYS;
	const isbillings = [BILLINGS.PAYMENTS, BILLINGS.INVOICES].includes(
		selectedSubModule?.key,
	);
	const isInvoice = selectedSubModule?.key === BILLINGS.INVOICES;
	const isCatalog = selectedSubModule?.key === INVENTORY.ITEMS.CONTROL;
	const isTransactions =
		selectedSubModule?.key === INVENTORY.TRANSACTIONS.CONTROL;
	const canShowBillingSummary =
		isbillings && canShowLegacyBillingSummaryAction({ profile, isInvoice });

	return (
		<Switch>
			<Route
				exact
				path={ROUTES.FEED_MODULE_ELEMENT.PATH}
				children={<Wrapper padding='0'></Wrapper>}
			/>
			<Route
				exact
				path={ROUTES.FEED_MODULE.PATH}
				children={
					<Wrapper padding='0'>
						<WarehouseSeriesControlToolbarAction
							screenKey={screenKey}
							datasourceStatus={datasourceStatus}
							datasourceSingleData={datasourceSingleData}
						/>
						{isCatalog && selectedItems?.length > 0 && (
							<DeleteCatalogItem itemIds={selectedItems} />
						)}
						{canShowMoveCatalogItemAction({ isCatalog, selectedItems }) && (
							<MoveCatalogItem itemId={selectedItems[0]} />
						)}
						<DatasourceRefresh />
						<DataFilterSearchValue />
						<SortPicker />
						{!isTransactions && <DataExport datasource={datasource} />}
						{canShowBillingSummary && selectedItems?.length > 0 && (
							<DataResumen paycutIds={selectedItems} isInvoice={isInvoice} />
						)}
						<DisplayPicker />
					</Wrapper>
				}
			/>
			<Route
				path={ROUTES.FEED.PATH}
				children={<Wrapper padding='0'></Wrapper>}
			/>
		</Switch>
	);
};

const DefaultToolbarLayout = ({
	menuIsOpen,
	history,
	dispatch,
	isMobile,
	datasource,
	screenKey,
	datasourceStatus,
	datasourceSingleData,
	theme,
	isDarkTheme,
}) => (
	<Wrapper
		padding='10px 0'
		width='100%'
		height='56px'
		justifyContent='space-between'
		backgroundColor={
			isDarkTheme
				? theme._secondaryBackgroundColor
				: theme._primaryBackgroundColor
		}
	>
		<LeftSideSwitcher
			menuIsOpen={menuIsOpen}
			history={history}
			dispatch={dispatch}
		/>
		<CenterSideSwitcher />
		<Wrapper padding='0'>
			<RightSideSwitcher
				datasource={datasource}
				screenKey={screenKey}
				datasourceStatus={datasourceStatus}
				datasourceSingleData={datasourceSingleData}
			/>
			<Notifications isMobile={isMobile} />
		</Wrapper>
	</Wrapper>
);

const ToolbarBuilder = ({
	menuIsOpen,
	screenKey,
	elementKey,
	isMobile,
	datasource,
	datasourceStatus,
	datasourceSingleData,
}) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const { theme, isDarkTheme } = useTheme();

	return (
		<Switch>
			{Object.keys(fullToolbars).map(path => {
				const FullToolbar = fullToolbars[path];
				if (!FullToolbar) return null;
				return (
					<Route
						exact
						key={path}
						path={path}
						children={
							<FullToolbar
								menuIsOpen={menuIsOpen}
								isMobile={isMobile}
								theme={theme}
								isDarkTheme={isDarkTheme}
							/>
						}
					/>
				);
			})}
			<Route
				path={ROUTES.FEED.PATH}
				children={
					<DefaultToolbarLayout
						menuIsOpen={menuIsOpen}
						history={history}
						dispatch={dispatch}
						isMobile={isMobile}
						datasource={datasource}
						screenKey={screenKey}
						datasourceStatus={datasourceStatus}
						datasourceSingleData={datasourceSingleData}
						theme={theme}
						isDarkTheme={isDarkTheme}
					/>
				}
			/>
		</Switch>
	);
};

export default ToolbarBuilder;
